import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ThemeProvider } from 'styled-components';
import { ThemeContext } from '../ThemeProvider/ThemeProvider';
import BasicIcon from '../BasicIcon/BasicIcon';
import {
  BasicIconContainer,
  Content,
  ContentText,
  IconBackground,
  Pipe,
  SeeMoreContainer,
  StatusStepperWrapper,
  StepButtonContainer,
  StepContainer,
  StepInfoContainer,
  Subtitle,
  TextContainer,
  Title,
  TitleSubtitleContainer,
} from './styles';
import { StatusStepperProps } from './types';
import Button from '../Button/Button';

export const StatusStepper: React.FC<StatusStepperProps> = ({
  status,
  orientation = 'downward',
  labelSeeMoreLess = { seeMore: 'ver más', seeLess: 'ver menos' },
  width,
}) => {
  const [expanded, setExpanded] = useState<Number>();
  const itemsRef = useRef<HTMLDivElement[]>([]);
  const { actualTheme } = useContext(ThemeContext);
  const { lineHeight } = actualTheme.statusStepper;
  const [pipeLenghts, setPipeLenghts] = useState([]);

  const getElement = (i: number) => {
    const elem = itemsRef.current[i];
    if (elem) {
      const child = elem.children[0];
      if (child) return child;
    }
    return null;
  };

  const arrayStatus = useMemo(
    () => (orientation === 'upward' ? [...status].slice(0).reverse() : [...status]),
    [orientation, status],
  );

  const isExpandable = useCallback(
    (i: number) => {
      const paragraph = getElement(i);
      if (paragraph) return paragraph.scrollHeight > 2 * lineHeight;

      return false;
    },
    [lineHeight],
  );

  const pipeLength = useCallback(
    (i) => {
      const element = getElement(i);
      const baseHeight = status[i].button ? actualTheme.statusStepper.buttonOffSet : 0;
      if (element) {
        if (expanded === i) {
          return baseHeight + element.scrollHeight + actualTheme.statusStepper.scrollHeightOffSet;
        }
        if (status[i].content) {
          if (element.scrollHeight === lineHeight)
            return baseHeight + actualTheme.statusStepper.singleLineHeight;
          return baseHeight + actualTheme.statusStepper.isExpandibleHeight;
        }
      }
      return baseHeight + actualTheme.statusStepper.defaultHeight;
    },
    [actualTheme, expanded, lineHeight, status],
  );

  useEffect(() => {
    const auxArray = [...pipeLenghts];
    status.forEach((_, index) => {
      auxArray[index] = pipeLength(index);
    });
    setPipeLenghts([...auxArray]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipeLength, status]);

  const onClickSeeMoreCallBack = useCallback(
    (i) => () => setExpanded((prev) => (prev === i ? -1 : i)),
    [],
  );

  return (
    <ThemeProvider theme={actualTheme}>
      <StatusStepperWrapper>
        {arrayStatus.map((step, i) => (
          <StepContainer>
            <StepInfoContainer>
              <BasicIconContainer>
                <IconBackground status={step.status}>
                  <BasicIcon
                    name={step.status === 'success' ? 'check-circle' : 'x-circle'}
                    color={
                      step.status === 'success'
                        ? actualTheme.statusStepper.success
                        : actualTheme.statusStepper.cancel
                    }
                  />
                </IconBackground>
              </BasicIconContainer>
              <TextContainer>
                <TitleSubtitleContainer>
                  <Title>{step.title}</Title>
                  <Subtitle>{step.subtitle}</Subtitle>
                  {step.button && (
                    <StepButtonContainer>
                      <Button
                        label={step.button.label}
                        // eslint-disable-next-line react/jsx-no-bind
                        onClick={() => step.button.action()}
                        hierarchy="link"
                      />
                    </StepButtonContainer>
                  )}
                </TitleSubtitleContainer>
                <Content
                  width={width}
                  // eslint-disable-next-line react/jsx-no-bind
                  ref={(el) => {
                    if (el) itemsRef.current[i] = el;
                  }}
                >
                  <ContentText expanded={i === expanded}>{step.content}</ContentText>

                  {isExpandable(i) && (
                    <SeeMoreContainer>
                      <Button
                        hierarchy="link"
                        label={expanded !== i ? labelSeeMoreLess.seeMore : labelSeeMoreLess.seeLess}
                        onClick={onClickSeeMoreCallBack(i)}
                        size="small"
                      />
                    </SeeMoreContainer>
                  )}
                </Content>
              </TextContainer>
            </StepInfoContainer>
            <Pipe
              isLastElement={i === status.length - 1}
              status={step.status}
              height={pipeLenghts[i]}
            />
          </StepContainer>
        ))}
      </StatusStepperWrapper>
    </ThemeProvider>
  );
};
